import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { API_URL } from '../../lib/constants';

const BASE_INTERVAL_MS = 5 * 60_000;
const MAX_INTERVAL_MS = 30 * 60_000;
const BACKOFF_FACTOR = 2;

const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;

type TavusHealthStatus = 'ok' | 'degraded' | 'down';

interface TavusHealthCheckResult {
  ok: boolean;
  message?: string;
}

interface TavusHealthSnapshot {
  status: TavusHealthStatus;
  checks: {
    statusPage: TavusHealthCheckResult;
    tavusProbe: TavusHealthCheckResult;
    llmProbe: TavusHealthCheckResult;
  };
  checkedAt: string;
}

interface TavusStatusIndicatorProps {
  accessToken?: string;
}

const STATUS_COLORS: Record<TavusHealthStatus | 'checking' | 'unknown', string> = {
  checking: '#9e9e9e',
  ok: '#2e7d32',
  degraded: '#ed6c02',
  down: '#d32f2f',
  unknown: '#757575',
};

function formatCheckLabel(name: string, check: TavusHealthCheckResult): string {
  const status = check.ok ? 'OK' : 'Failed';
  const detail = check.message ? `: ${check.message}` : '';
  return `${name} — ${status}${detail}`;
}

function formatCheckedAt(iso: string | null): string {
  if (!iso) return 'Never';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

const TavusStatusIndicator: React.FC<TavusStatusIndicatorProps> = ({ accessToken }) => {
  const [snapshot, setSnapshot] = useState<TavusHealthSnapshot | null>(null);
  const [checking, setChecking] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const currentIntervalRef = useRef(BASE_INTERVAL_MS / BACKOFF_FACTOR);
  const activitySinceLastPollRef = useRef(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchHealth = useCallback(async () => {
    if (!accessToken) {
      return;
    }

    setChecking(true);
    setFetchError(null);

    try {
      const response = await fetch(`${API_URL}/tavus/health`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Health check failed with status ${response.status}`);
      }

      const data = await response.json() as TavusHealthSnapshot;
      setSnapshot(data);
    } catch (error) {
      console.error('[TavusStatusIndicator] Failed to fetch health:', error);
      setFetchError(error instanceof Error ? error.message : 'Unable to reach health endpoint');
    } finally {
      setChecking(false);
    }
  }, [accessToken]);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }

    const clearScheduledPoll = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const scheduleNext = () => {
      clearScheduledPoll();

      const nextInterval = activitySinceLastPollRef.current
        ? BASE_INTERVAL_MS
        : Math.min(currentIntervalRef.current * BACKOFF_FACTOR, MAX_INTERVAL_MS);

      currentIntervalRef.current = nextInterval;
      timeoutRef.current = setTimeout(() => {
        void tick();
      }, nextInterval);
    };

    const tick = async () => {
      if (document.hidden) {
        return;
      }

      await fetchHealth();
      activitySinceLastPollRef.current = false;
      scheduleNext();
    };

    const handleActivity = () => {
      activitySinceLastPollRef.current = true;
      currentIntervalRef.current = BASE_INTERVAL_MS;
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        clearScheduledPoll();
        return;
      }

      currentIntervalRef.current = BASE_INTERVAL_MS;
      activitySinceLastPollRef.current = true;
      void fetchHealth().finally(() => {
        activitySinceLastPollRef.current = false;
        scheduleNext();
      });
    };

    currentIntervalRef.current = BASE_INTERVAL_MS / BACKOFF_FACTOR;
    activitySinceLastPollRef.current = false;

    void fetchHealth().finally(() => {
      activitySinceLastPollRef.current = false;
      scheduleNext();
    });

    for (const eventName of ACTIVITY_EVENTS) {
      window.addEventListener(eventName, handleActivity, { passive: true });
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearScheduledPoll();
      for (const eventName of ACTIVITY_EVENTS) {
        window.removeEventListener(eventName, handleActivity);
      }
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [accessToken, fetchHealth]);

  const visualStatus: TavusHealthStatus | 'checking' | 'unknown' = checking && !snapshot
    ? 'checking'
    : snapshot?.status ?? (fetchError ? 'unknown' : 'checking');

  const dotColor = STATUS_COLORS[visualStatus];

  const tooltipContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, maxWidth: 360 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        Video call readiness
      </Typography>
      {fetchError && (
        <Typography variant="body2" color="error">
          {fetchError}
        </Typography>
      )}
      {snapshot ? (
        <>
          <Typography variant="body2">
            Overall: {snapshot.status.toUpperCase()}
          </Typography>
          <Typography variant="body2">
            {formatCheckLabel('Tavus API probe', snapshot.checks.tavusProbe)}
          </Typography>
          <Typography variant="body2">
            {formatCheckLabel('Custom LLM', snapshot.checks.llmProbe)}
          </Typography>
          <Typography variant="body2">
            {formatCheckLabel('Tavus status page', snapshot.checks.statusPage)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Last checked: {formatCheckedAt(snapshot.checkedAt)}
          </Typography>
        </>
      ) : (
        <Typography variant="body2">
          {checking ? 'Running live health checks…' : 'Waiting for health data…'}
        </Typography>
      )}
    </Box>
  );

  return (
    <Tooltip title={tooltipContent} arrow placement="right">
      <Box
        component="span"
        role="status"
        aria-label={`Tavus video call status: ${visualStatus}`}
        sx={{
          display: 'inline-block',
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: dotColor,
          boxShadow: '0 0 0 2px rgba(255,255,255,0.8)',
          cursor: 'help',
          flexShrink: 0,
        }}
      />
    </Tooltip>
  );
};

export default TavusStatusIndicator;
